/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
import React from 'react';

import { MapTo } from '@adobe/aem-react-editable-components';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

import extractModelId from 'utils/extract-model-id';

import './css/Text.scss';

/**
 * Default Edit configuration for the Text component that interact with the Core Text component and sub-types
 *
 * @type EditConfig
 */
const TextEditConfig = {
  emptyLabel: 'Text',

  isEmpty(props) {
    return !props || !props.text || props.text.trim().length < 1;
  },
};

/**
 * Text React component
 */
export const Text = (props) => {
  const { cqPath, text, richText } = props;

  const richTextContent = (
    <div
      className="efix-richtext-content"
      id={extractModelId(cqPath)}
      data-rte-editelement
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(text),
      }}
    />
  );

  const textContent = <div>{text}</div>;

  return richText ? richTextContent : textContent;
};

MapTo('e-fix/components/content/text')(Text, TextEditConfig);

Text.propTypes = {
  cqPath: PropTypes.string,
  text: PropTypes.string,
  richText: PropTypes.bool,
};

Text.defaultProps = {
  cqPath: '',
  text: '',
  richText: false,
};
